<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { getAmountLabel, getAmountValue } from "@/helpers/remuneration";
import { useConfigurationStore } from "@/store/configurationStore";

import type { Remuneration, RemunerationAmountOption, RemunerationPeriodicity } from "@/types/remuneration";

import ButtonAlt from "@/dsComponents/buttons/ButtonAlt.vue";
import ComboButton from "@/dsComponents/buttons/ComboButton.vue";
import Dropdown from "@/dsComponents/Dropdown/Dropdown.vue";
import DropdownItem from "@/dsComponents/Dropdown/DropdownItem.vue";
import TextInputAlt from "@/dsComponents/inputs/TextInputAlt.vue";

import { InputMode } from "@/components/dropdown/types";

interface RemunerationDropdownProps {
    modelValue: Remuneration;
    placeholder?: string;
    showIcon?: boolean;
    icon?: string;
    error?: boolean;
    size?: "xs" | "sm";
    variant?: "stroke" | "ghost";
    width?: string;
    fullWidth?: boolean;
    inputMode?: InputMode;
}

const props = withDefaults(defineProps<RemunerationDropdownProps>(), {
    width: "260px",
    showIcon: true,
    icon: "money",
    fullWidth: false,
    inputMode: InputMode.DROPDOWN,
});

const emit = defineEmits<(event: "update:modelValue", value: Remuneration) => void>();

const { t } = useI18n();
const configStore = useConfigurationStore();

const periodicity = ref<RemunerationPeriodicity | null>(props.modelValue.periodicity);
const minimum = ref<number | null>(props.modelValue.minimumAmount);
const maximum = ref<number | null>(props.modelValue.maximumAmount);

const open = ref(false);
const periodicityOpen = ref(false);
const minimumOpen = ref(false);
const maximumOpen = ref(false);

const remunerationPeriodicityOptions = computed(() => configStore.remunerationPeriodicityOptions);

const getPeriodicityOption = (value: RemunerationPeriodicity | null) => {
    if (!value) return undefined;
    return remunerationPeriodicityOptions.value.find((option) => option.value === value);
};

const minimumValues = computed(() => {
    return getPeriodicityOption(periodicity.value)?.minimumValues ?? [];
});

const maximumValues = computed(() => {
    return getPeriodicityOption(periodicity.value)?.maximumValues ?? [];
});

const hasValue = computed(() => {
    return props.modelValue.periodicity && ((props.modelValue.minimumAmount ?? 0) > 0 || (props.modelValue.maximumAmount ?? 0) > 0);
});

const displayValue = computed(() => {
    if (!hasValue.value) return props.placeholder ?? t("dropdowns.remuneration.placeholder._default");
    const periodicityLabel = getPeriodicityOption(props.modelValue.periodicity)?.label ?? t("global.notDefined");
    if (!minimum.value && !maximum.value) {
        return periodicityLabel;
    }
    return `${periodicityLabel} / ${minimum.value ?? 0}€ - ${maximum.value ?? 0}€`;
});

const handleToggle = (newValue: boolean) => {
    open.value = newValue;
};

const handlePeriodicityToggle = (newValue: boolean) => {
    periodicityOpen.value = newValue;
};

const handleMinimumToggle = (newValue: boolean) => {
    minimumOpen.value = newValue;
};

const handleMaximumToggle = (newValue: boolean) => {
    maximumOpen.value = newValue;
};

const isMaximumInvalid = computed(() => {
    return maximum.value !== null && minimum.value !== null && maximum.value < minimum.value;
});

const handlePeriodicitySelect = (newPeriodicity: RemunerationPeriodicity) => {
    periodicity.value = newPeriodicity;
    periodicityOpen.value = false;
    minimum.value = null;
    maximum.value = null;
};

const handleMaximumSelect = (maximumOption: RemunerationAmountOption) => {
    maximum.value = getAmountValue(maximumOption);
    maximumOpen.value = false;
};

const handleMinimumSelect = (minimumOption: RemunerationAmountOption) => {
    minimum.value = getAmountValue(minimumOption);
    minimumOpen.value = false;
};

const handleMinimumInput = (value: string) => {
    minimum.value = parseFloat(value) || 0;
};

const handleMaximumInput = (value: string) => {
    maximum.value = parseFloat(value) || 0;
};

const handleCancel = () => {
    periodicity.value = props.modelValue.periodicity;
    minimum.value = props.modelValue.minimumAmount;
    maximum.value = props.modelValue.maximumAmount;
    open.value = false;
};

const handleValidate = () => {
    open.value = false;
    emit("update:modelValue", {
        periodicity: periodicity.value,
        minimumAmount: minimum.value,
        maximumAmount: maximum.value,
    });
};

watch(
    () => props.modelValue,
    (newValue) => {
        if (newValue) {
            periodicity.value = newValue.periodicity;
            minimum.value = newValue.minimumAmount;
            maximum.value = newValue.maximumAmount;
        }
    },
    { deep: true }
);
</script>

<template>
    <Dropdown :open="open" :width="width" @toggle="handleToggle">
        <template #trigger-zone>
            <ComboButton :icon-left="showIcon ? icon : undefined" :error="error" :placeholder="!hasValue" :fullWidth="fullWidth" :variant="variant">
                {{ displayValue }}
            </ComboButton>
        </template>
        <template #dropdown-content>
            <div class="remuneration-dropdown__content">
                <div class="remuneration-dropdown__content__item">
                    <div class="remuneration-dropdown__content__item__label typo-xxs-regular">
                        {{ t("dropdowns.remuneration.form.labels.periodicity") }}
                    </div>
                    <Dropdown :open="periodicityOpen" @toggle="handlePeriodicityToggle">
                        <template #trigger-zone>
                            <ComboButton
                                :placeholder="!getPeriodicityOption(periodicity)?.label"
                                variant="stroke"
                                iconRight="chevron-down"
                                class="remuneration-dropdown__periodicity-button"
                            >
                                {{ getPeriodicityOption(periodicity)?.label ?? t("dropdowns.remuneration.placeholder.periodicity") }}
                            </ComboButton>
                        </template>
                        <template #dropdown-content>
                            <DropdownItem
                                v-for="option in remunerationPeriodicityOptions"
                                :key="option.value"
                                :checked="periodicity === option.value"
                                @click="() => handlePeriodicitySelect(option.value)"
                                stop
                            >
                                {{ option.label }}
                            </DropdownItem>
                        </template>
                    </Dropdown>
                </div>
                <div class="remuneration-dropdown__content__item">
                    <div class="remuneration-dropdown__content__item__label typo-xxs-regular">
                        {{ t("dropdowns.remuneration.form.labels.minimum") }}
                    </div>
                    <Dropdown v-if="inputMode === InputMode.DROPDOWN" :open="minimumOpen" @toggle="handleMinimumToggle">
                        <template #trigger-zone>
                            <ComboButton
                                :placeholder="!minimum"
                                variant="stroke"
                                iconLeft="currency-euro"
                                iconRight="chevron-down"
                                class="remuneration-dropdown__amount-button"
                            >
                                {{ minimum ?? t("dropdowns.remuneration.placeholder.minimum") }}
                            </ComboButton>
                        </template>
                        <template #dropdown-content>
                            <DropdownItem
                                v-for="minimumValue in minimumValues"
                                :key="`min-${getAmountValue(minimumValue)}`"
                                :checked="minimum === getAmountValue(minimumValue)"
                                @click="() => handleMinimumSelect(minimumValue)"
                                stop
                            >
                                {{ getAmountLabel(minimumValue) }}
                            </DropdownItem>
                        </template>
                    </Dropdown>
                    <TextInputAlt
                        v-else
                        type="number"
                        variant="stroke"
                        icon-left="currency-euro"
                        :model-value="minimum?.toString() ?? ''"
                        :placeholder="t('dropdowns.remuneration.placeholder.minimum')"
                        @update:model-value="handleMinimumInput"
                    />
                </div>
                <div class="remuneration-dropdown__content__item">
                    <div class="remuneration-dropdown__content__item__label typo-xxs-regular">
                        {{ t("dropdowns.remuneration.form.labels.maximum") }}
                    </div>
                    <Dropdown v-if="inputMode === InputMode.DROPDOWN" :open="maximumOpen" @toggle="handleMaximumToggle">
                        <template #trigger-zone>
                            <ComboButton
                                :placeholder="!maximum"
                                variant="stroke"
                                iconLeft="currency-euro"
                                iconRight="chevron-down"
                                class="remuneration-dropdown__amount-button"
                                :error="isMaximumInvalid"
                            >
                                {{ maximum ?? t("dropdowns.remuneration.placeholder.maximum") }}
                            </ComboButton>
                        </template>
                        <template #dropdown-content>
                            <DropdownItem
                                v-for="maximumValue in maximumValues"
                                :key="`max-${getAmountValue(maximumValue)}`"
                                :checked="maximum === getAmountValue(maximumValue)"
                                @click="() => handleMaximumSelect(maximumValue)"
                                stop
                            >
                                {{ getAmountLabel(maximumValue) }}
                            </DropdownItem>
                        </template>
                    </Dropdown>
                    <TextInputAlt
                        v-else
                        type="number"
                        variant="stroke"
                        icon-left="currency-euro"
                        :model-value="maximum?.toString() ?? ''"
                        :placeholder="t('dropdowns.remuneration.placeholder.maximum')"
                        :error="isMaximumInvalid"
                        @update:model-value="handleMaximumInput"
                    />
                </div>
            </div>
        </template>
        <template #dropdown-footer>
            <div class="remuneration-dropdown__content__footer">
                <ButtonAlt :label="t('global.actions.cancel')" size="sm" type="neutral" @click="handleCancel" />
                <ButtonAlt
                    :label="t('global.actions.validate')"
                    size="sm"
                    variant="filled"
                    type="neutral"
                    :disabled="isMaximumInvalid"
                    @click="handleValidate"
                />
            </div>
        </template>
    </Dropdown>
</template>
<style scoped lang="scss">
.remuneration-dropdown {
    &__periodicity-button {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    &__amount-button {
        width: 100%;
        display: flex;
        justify-content: flex-start;
        align-items: center;

        &::v-deep(.ti) {
            margin-left: auto;
        }
    }

    &__content {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md, 8px);
        padding: var(--spacing-md, 8px);

        &__item {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-xs, 4px);
        }

        &__footer {
            display: flex;
            justify-content: flex-end;
            gap: var(--spacing-md, 8px);
            padding: var(--spacing-lg, 12px) var(--spacing-xs, 4px) var(--spacing-xs, 4px);
        }
    }
}
</style>
